import React, { useEffect, useState } from 'react';
import {
  Search,
  Loader2,
  AlertCircle,
  ArrowRight,
  Sparkles,
  Building2,
  Mail,
  ChevronLeft,
  Home,
  TrendingUp,
} from 'lucide-react';
import { lookupPostcode, isLikelyUkPostcode } from '../services/postcode';
import { findAddressesByPostcode, formatAddress } from '../services/properties';
import { getValuation, formatMoney, formatMonthYear } from '../services/valuation';
import { isConfigured } from '../services/supabase';
import { APP_TAGLINE } from '../constants';
import type { AddressOption, BrandConfig, ValuationResult } from '../types';
import LeadForm from './LeadForm';

interface LandingHeroProps {
  onSignUp: () => void;
  brand?: BrandConfig | null;
}

type Step = 'POSTCODE' | 'ADDRESS' | 'NO_COVERAGE' | 'CONFIRMED';

function UnionJack({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 60 30"
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      aria-label="United Kingdom"
      role="img"
    >
      <clipPath id="uk-jack-clip-vm">
        <path d="M0,0 v30 h60 v-30 z" />
      </clipPath>
      <clipPath id="uk-jack-stclip-vm">
        <path d="M30,15 h30 v15 z v15 h-30 z h-30 v-15 z v-15 h30 z" />
      </clipPath>
      <g clipPath="url(#uk-jack-clip-vm)">
        <rect width="60" height="30" fill="#012169" />
        <path d="M0,0 L60,30 M60,0 L0,30" stroke="#fff" strokeWidth="6" />
        <path
          d="M0,0 L60,30 M60,0 L0,30"
          clipPath="url(#uk-jack-stclip-vm)"
          stroke="#C8102E"
          strokeWidth="4"
        />
        <path d="M30,0 v30 M0,15 h60" stroke="#fff" strokeWidth="10" />
        <path d="M30,0 v30 M0,15 h60" stroke="#C8102E" strokeWidth="6" />
      </g>
    </svg>
  );
}

export default function LandingHero({ onSignUp, brand }: LandingHeroProps) {
  const accent = brand?.primary_colour || '#f47a6b';
  const isBranded = !!brand;
  const [step, setStep] = useState<Step>('POSTCODE');
  const [postcode, setPostcode] = useState('');
  const [resolvedPostcode, setResolvedPostcode] = useState<string | null>(null);
  const [resolvedArea, setResolvedArea] = useState<string | undefined>(undefined);
  const [addresses, setAddresses] = useState<AddressOption[]>([]);
  const [selectedAddress, setSelectedAddress] = useState<AddressOption | null>(null);
  const [looking, setLooking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setStep('POSTCODE');
    setError(null);
    setAddresses([]);
    setSelectedAddress(null);
    setResolvedPostcode(null);
    setResolvedArea(undefined);
  };

  const submitPostcode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const trimmed = postcode.trim();
    if (!trimmed) {
      setError('Enter your postcode to get started.');
      return;
    }
    if (!isLikelyUkPostcode(trimmed)) {
      setError("That doesn't look like a UK postcode. Try e.g. SW1A 1AA.");
      return;
    }

    setLooking(true);
    try {
      // 1. Validate postcode via postcodes.io (free, keyless)
      const pc = await lookupPostcode(trimmed);
      if (!pc) {
        setError("We couldn't find that postcode. Double-check the spelling?");
        return;
      }
      setResolvedPostcode(pc.postcode);
      setResolvedArea(pc.admin_district);

      // 2. Find addresses on that postcode in our database
      const found = await findAddressesByPostcode(pc.postcode);
      if (found.length === 0) {
        setStep('NO_COVERAGE');
        return;
      }
      setAddresses(found);
      setStep('ADDRESS');
    } finally {
      setLooking(false);
    }
  };

  const confirmAddress = (a: AddressOption) => {
    setSelectedAddress(a);
    setStep('CONFIRMED');
  };

  return (
    <main className="flex-1">
      <div className="container mx-auto px-4 py-16 md:py-24">
        <div className="max-w-3xl mx-auto text-center">
          {/* Eyebrow */}
          <div
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-medium mb-8 border"
            style={{
              background: hexWithAlpha(accent, 0.1),
              borderColor: hexWithAlpha(accent, 0.25),
              color: hexLighten(accent),
            }}
          >
            <UnionJack className="w-4 h-auto shrink-0 rounded-sm shadow-sm ring-1 ring-white/10" />
            {isBranded
              ? `Property valuations from ${brand!.brand_name}`
              : 'Property valuations for UK estate agents'}
          </div>

          {/* Logo lockup — agent's logo if branded, ours otherwise */}
          <div className="relative flex justify-center mb-8 min-h-[3rem] items-center">
            <div className="coral-glow absolute inset-0 -m-12 pointer-events-none" />
            {isBranded && brand?.logo_url ? (
              <img
                src={brand.logo_url}
                alt={brand.brand_name}
                className="relative max-h-20 md:max-h-24 w-auto object-contain"
              />
            ) : isBranded ? (
              <div
                className="relative text-2xl md:text-3xl font-bold tracking-tight text-white"
                style={{ color: accent }}
              >
                {brand!.brand_name}
              </div>
            ) : (
              <img
                src="/Logo.png"
                alt="Valmate by ReHub"
                className="relative h-[4.125rem] md:h-[5.5rem] w-auto object-contain"
              />
            )}
          </div>

          {/* Headline */}
          <h1 className="text-4xl md:text-6xl font-bold leading-[1.05] tracking-tight mb-5 text-white">
            Know what your home is worth.
          </h1>

          <p className="text-lg md:text-xl text-slate-400 mb-10 max-w-2xl mx-auto leading-relaxed">
            {isBranded
              ? `Free instant valuation, then a real one from ${brand!.brand_name}.`
              : APP_TAGLINE}
          </p>

          {/* Multi-step address resolver */}
          <div className="max-w-xl mx-auto">
            {step === 'POSTCODE' && (
              <PostcodeStep
                postcode={postcode}
                setPostcode={setPostcode}
                onSubmit={submitPostcode}
                looking={looking}
                error={error}
                accent={accent}
              />
            )}

            {step === 'ADDRESS' && resolvedPostcode && (
              <AddressStep
                postcode={resolvedPostcode}
                area={resolvedArea}
                addresses={addresses}
                onPick={confirmAddress}
                onBack={reset}
                accent={accent}
              />
            )}

            {step === 'NO_COVERAGE' && resolvedPostcode && (
              <NoCoverageStep
                postcode={resolvedPostcode}
                supabaseConfigured={isConfigured}
                onSignUp={onSignUp}
                onBack={reset}
                accent={accent}
              />
            )}

            {step === 'CONFIRMED' && selectedAddress && (
              <ConfirmedStep
                address={selectedAddress}
                onBack={() => setStep('ADDRESS')}
                accent={accent}
                brand={brand ?? null}
              />
            )}
          </div>

          {/* Sub-CTA for agents — only on the unbranded homepage */}
          {!isBranded && step === 'POSTCODE' && (
            <div className="mt-10 text-sm text-slate-500 flex flex-wrap items-center justify-center gap-2">
              <span>Are you an estate agent?</span>
              <button
                onClick={onSignUp}
                className="inline-flex items-center gap-1 font-semibold transition-colors hover:underline"
                style={{ color: accent }}
              >
                Embed Valmate on your site
                <ArrowRight size={12} />
              </button>
            </div>
          )}

          {/* Feature trio */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 max-w-3xl mx-auto mt-16">
            <FeatureCard
              icon={<Search size={20} />}
              title="Postcode in, address out"
              body="We list every property on your postcode so you pick the exact one — not just the street."
            />
            <FeatureCard
              icon={<Building2 size={20} />}
              title="Real comparables"
              body="See three to five recent nearby sales — the same evidence your local agent would use."
            />
            <FeatureCard
              icon={<Mail size={20} />}
              title="One step from a real valuation"
              body="Captured leads land straight in the agent's inbox. Booked valuations in 24 hours."
            />
          </div>
        </div>
      </div>
    </main>
  );
}

/* -------------------------------------------------------------------------- */
/*  Step 1: postcode                                                          */
/* -------------------------------------------------------------------------- */

function PostcodeStep({
  postcode,
  setPostcode,
  onSubmit,
  looking,
  error,
  accent,
}: {
  postcode: string;
  setPostcode: (s: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  looking: boolean;
  error: string | null;
  accent: string;
}) {
  return (
    <form onSubmit={onSubmit} aria-label="Get an instant valuation">
      <div className="flex flex-col sm:flex-row gap-2 p-2 rounded-2xl bg-slate-900/80 border border-slate-800 backdrop-blur shadow-xl shadow-black/30">
        <div className="flex-1 flex items-center gap-2 px-3">
          <Search size={18} className="text-slate-500 shrink-0" />
          <input
            type="text"
            value={postcode}
            onChange={(e) => setPostcode(e.target.value.toUpperCase())}
            placeholder="Enter your postcode (e.g. SW1A 1AA)"
            autoComplete="postal-code"
            className="flex-1 bg-transparent py-3 text-base text-white placeholder-slate-500 focus:outline-none"
          />
        </div>
        <button
          type="submit"
          disabled={looking}
          className="flex items-center justify-center gap-2 px-6 py-3 text-white font-semibold rounded-xl disabled:opacity-50 transition-colors"
          style={{ background: accent }}
        >
          {looking ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
          Find my home
        </button>
      </div>

      {error && (
        <div className="flex items-center gap-2 mt-3 text-sm text-rose-300 justify-center">
          <AlertCircle size={14} className="shrink-0" />
          <span>{error}</span>
        </div>
      )}
    </form>
  );
}

/* -------------------------------------------------------------------------- */
/*  Step 2: address picker                                                    */
/* -------------------------------------------------------------------------- */

function AddressStep({
  postcode,
  area,
  addresses,
  onPick,
  onBack,
  accent,
}: {
  postcode: string;
  area?: string;
  addresses: AddressOption[];
  onPick: (a: AddressOption) => void;
  onBack: () => void;
  accent: string;
}) {
  return (
    <div className="p-6 rounded-2xl bg-slate-900/80 border border-slate-800 text-left animate-fade-in">
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={onBack}
          className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-white transition-colors"
        >
          <ChevronLeft size={14} />
          Different postcode
        </button>
        <div
          className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider font-semibold"
          style={{ color: hexLighten(accent) }}
        >
          <Building2 size={13} />
          {postcode}
          {area ? ` · ${area}` : ''}
        </div>
      </div>

      <h3 className="text-white font-semibold text-lg mb-1">
        Which one's yours?
      </h3>
      <p className="text-sm text-slate-400 mb-4">
        We found{' '}
        <span className="text-slate-200 font-semibold">{addresses.length}</span>{' '}
        propert{addresses.length === 1 ? 'y' : 'ies'} on this postcode.
      </p>

      <div className="max-h-72 overflow-y-auto -mx-2 px-2 space-y-1.5">
        {addresses.map((a) => (
          <button
            key={a.uprn}
            onClick={() => onPick(a)}
            className="w-full text-left flex items-center gap-3 px-3 py-2.5 rounded-lg bg-slate-950/60 border border-slate-800 hover:bg-slate-900 transition-colors group"
            onMouseOver={(e) => (e.currentTarget.style.borderColor = accent)}
            onMouseOut={(e) => (e.currentTarget.style.borderColor = '')}
          >
            <Home size={16} className="text-slate-500 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-sm text-white truncate">{formatAddress(a)}</div>
              {a.property_type && (
                <div className="text-xs text-slate-500 capitalize">{a.property_type}</div>
              )}
            </div>
            <ArrowRight size={14} className="text-slate-600 shrink-0" />
          </button>
        ))}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Step 3a: no coverage                                                      */
/* -------------------------------------------------------------------------- */

function NoCoverageStep({
  postcode,
  supabaseConfigured,
  onSignUp,
  onBack,
  accent,
}: {
  postcode: string;
  supabaseConfigured: boolean;
  onSignUp: () => void;
  onBack: () => void;
  accent: string;
}) {
  return (
    <div className="p-6 rounded-2xl bg-slate-900/80 border border-slate-800 text-left animate-fade-in">
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={onBack}
          className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-white transition-colors"
        >
          <ChevronLeft size={14} />
          Different postcode
        </button>
        <div
          className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider font-semibold"
          style={{ color: hexLighten(accent) }}
        >
          <Building2 size={13} />
          {postcode}
        </div>
      </div>

      <h3 className="text-white font-semibold text-lg mb-1">
        No sales on record for {postcode}.
      </h3>
      <p className="text-sm text-slate-400 mb-4">
        {supabaseConfigured
          ? "The Land Registry has no recorded sales for this postcode — usually means it's a brand-new postcode or hasn't been transacted since 1995. Your local agent can still give you a manual valuation."
          : "Valmate is in private beta. Estate agents can request early access below."}
      </p>
      <button
        onClick={onSignUp}
        className="inline-flex items-center gap-1.5 text-sm font-semibold transition-colors"
        style={{ color: accent }}
      >
        I'm an estate agent — get early access
        <ArrowRight size={14} />
      </button>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Step 3b: address confirmed → fetch & display valuation + lead form        */
/* -------------------------------------------------------------------------- */

function ConfirmedStep({
  address,
  onBack,
  accent,
  brand,
}: {
  address: AddressOption;
  onBack: () => void;
  accent: string;
  brand: BrandConfig | null;
}) {
  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState<ValuationResult | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getValuation(address.uprn).then((r) => {
      if (cancelled) return;
      setResult(r);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [address.uprn]);

  const fullAddress = formatAddress(address);

  return (
    <div className="p-6 rounded-2xl bg-slate-900/80 border border-slate-800 text-left animate-fade-in">
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={onBack}
          className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-white transition-colors"
        >
          <ChevronLeft size={14} />
          Wrong address
        </button>
        <div
          className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider font-semibold"
          style={{ color: hexLighten(accent) }}
        >
          <Home size={13} />
          {address.postcode}
        </div>
      </div>

      <div className="text-white font-semibold text-lg mb-1">{fullAddress}</div>
      {address.property_type && (
        <div className="text-xs text-slate-500 capitalize mb-5">
          {address.property_type}
        </div>
      )}

      {loading && (
        <div className="flex items-center justify-center gap-2 py-10 text-slate-400 text-sm">
          <Loader2 size={16} className="animate-spin" />
          Crunching the numbers…
        </div>
      )}

      {!loading && result && result.available && (
        <ValuationResultPanel
          uprn={address.uprn}
          postcode={address.postcode}
          addressSnapshot={fullAddress}
          result={result}
          accent={accent}
          brand={brand}
        />
      )}

      {!loading && (!result || !result.available) && (
        <NoValuationPanel
          uprn={address.uprn}
          postcode={address.postcode}
          addressSnapshot={fullAddress}
          reason={result && !result.available ? result.reason : null}
          accent={accent}
          brand={brand}
        />
      )}
    </div>
  );
}

function ValuationResultPanel({
  uprn,
  postcode,
  addressSnapshot,
  result,
  accent,
  brand,
}: {
  uprn: string;
  postcode: string;
  addressSnapshot: string;
  result: Extract<ValuationResult, { available: true }>;
  accent: string;
  brand: BrandConfig | null;
}) {
  return (
    <>
      <div className="p-5 rounded-xl bg-slate-950/60 border border-slate-800 mb-4">
        <div className="flex items-baseline justify-between mb-1.5">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
            Estimated value
          </div>
          <ConfidencePill confidence={result.confidence} />
        </div>
        <div
          className="text-3xl md:text-4xl font-bold tracking-tight"
          style={{ color: accent }}
        >
          {formatMoney(result.estimate_low)}
          <span className="text-slate-500 mx-2 text-2xl font-normal">–</span>
          {formatMoney(result.estimate_high)}
        </div>
        <div className="text-xs text-slate-500 mt-2 flex items-center gap-1.5">
          <TrendingUp size={12} />
          Based on a {formatMoney(result.last_sale_price)} sale in{' '}
          {formatMonthYear(result.last_sale_date)}, projected forward at{' '}
          {(result.assumed_annual_growth * 100).toFixed(1)}% / year.
        </div>
      </div>

      <div className="text-xs text-slate-500 leading-relaxed mb-4">
        This is a rough estimate based on public Land Registry data — it
        doesn't account for refurbishments, extensions or current condition. For
        an accurate valuation, ask{' '}
        {brand ? brand.brand_name : 'a local agent'} below.
      </div>

      <LeadForm
        uprn={uprn}
        postcode={postcode}
        addressSnapshot={addressSnapshot}
        estimate={result.estimate}
        estimateLow={result.estimate_low}
        estimateHigh={result.estimate_high}
        confidence={result.confidence}
        agentWidgetId={brand?.widget_id}
        brandColour={accent}
        brandName={brand?.brand_name}
      />
    </>
  );
}

function NoValuationPanel({
  uprn,
  postcode,
  addressSnapshot,
  reason,
  accent,
  brand,
}: {
  uprn: string;
  postcode: string;
  addressSnapshot: string;
  reason: 'unknown_property' | 'no_sale_history' | null;
  accent: string;
  brand: BrandConfig | null;
}) {
  return (
    <>
      <div className="p-5 rounded-xl bg-slate-950/60 border border-slate-800 mb-4">
        <div
          className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider font-semibold mb-2"
          style={{ color: hexLighten(accent) }}
        >
          <Sparkles size={12} />
          No instant estimate available
        </div>
        <p className="text-sm text-slate-400 leading-relaxed">
          {reason === 'no_sale_history'
            ? "We can't generate an estimate because the Land Registry has no sale on record for this property — usually because it hasn't changed hands since 1995. "
            : 'We need a bit more data on this property to generate an estimate. '}
          {brand
            ? `${brand.brand_name} can still give you an accurate valuation.`
            : 'A local agent can give you an accurate valuation.'}
        </p>
      </div>

      <LeadForm
        uprn={uprn}
        postcode={postcode}
        addressSnapshot={addressSnapshot}
        estimate={null}
        estimateLow={null}
        estimateHigh={null}
        confidence={null}
        agentWidgetId={brand?.widget_id}
        brandColour={accent}
        brandName={brand?.brand_name}
      />
    </>
  );
}

function ConfidencePill({ confidence }: { confidence: 'low' | 'medium' | 'high' }) {
  const styles =
    confidence === 'high'
      ? { bg: 'rgba(16, 185, 129, 0.12)', color: '#34d399', label: 'High confidence' }
      : confidence === 'medium'
      ? { bg: 'rgba(234, 179, 8, 0.12)', color: '#facc15', label: 'Medium confidence' }
      : {
          bg: 'rgba(148, 163, 184, 0.12)',
          color: '#94a3b8',
          label: 'Low confidence',
        };
  return (
    <span
      className="inline-flex items-center text-[10px] uppercase tracking-wider font-semibold px-2 py-1 rounded-full"
      style={{ background: styles.bg, color: styles.color }}
    >
      {styles.label}
    </span>
  );
}

/* -------------------------------------------------------------------------- */

function FeatureCard({
  icon,
  title,
  body,
}: {
  icon: React.ReactNode;
  title: string;
  body: string;
}) {
  return (
    <div className="text-left p-5 bg-slate-900/40 border border-slate-800 rounded-2xl hover:border-slate-700 transition-colors">
      <div
        className="w-10 h-10 rounded-xl flex items-center justify-center mb-3"
        style={{ background: 'rgba(244, 122, 107, 0.12)', color: '#f47a6b' }}
      >
        {icon}
      </div>
      <h3 className="text-white font-semibold text-sm mb-1">{title}</h3>
      <p className="text-slate-400 text-xs leading-relaxed">{body}</p>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Tiny colour helpers — let arbitrary agent-supplied hex colours render     */
/*  the soft eyebrow / pill backgrounds without needing tailwind classes.    */
/* -------------------------------------------------------------------------- */

function parseHex(hex: string): { r: number; g: number; b: number } {
  let h = hex.replace('#', '').trim();
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  if (h.length !== 6) return { r: 244, g: 122, b: 107 };
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  if ([r, g, b].some((n) => Number.isNaN(n))) {
    return { r: 244, g: 122, b: 107 };
  }
  return { r, g, b };
}

function hexWithAlpha(hex: string, alpha: number): string {
  const { r, g, b } = parseHex(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function hexLighten(hex: string): string {
  const { r, g, b } = parseHex(hex);
  const lighten = (c: number) => Math.min(255, Math.round(c + (255 - c) * 0.4));
  return `rgb(${lighten(r)}, ${lighten(g)}, ${lighten(b)})`;
}
